import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, ImageOff, Loader2, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { API } from "@/lib/api";

export function imagePreviewUrl(asset) {
  if (asset.stored_path) return `${API}/media/file/${asset.id}`;
  const cover = asset.cover_url;
  if (!cover) return null;
  // Drive supplies a small thumbnail for the grid. Request a larger rendition
  // only on Google's image hosts; leave all other source URLs unchanged.
  try {
    const url = new URL(cover);
    if (/(^|\.)(googleusercontent\.com|ggpht\.com)$/.test(url.hostname)) {
      return cover.replace(/=s\d+(?:-[a-z0-9-]+)?$/i, "=s1600");
    }
  } catch (_) { /* A relative cover URL is already usable as provided. */ }
  return cover;
}

function PreviewImage({ asset, t }) {
  const [src, setSrc] = useState(() => imagePreviewUrl(asset));
  const [loading, setLoading] = useState(Boolean(src));
  const [failed, setFailed] = useState(!src);
  if (failed) return (
    <div role="status" className="text-center text-slate-300 p-6">
      <ImageOff size={36} className="mx-auto mb-3" />
      <p>{t("Dieses Bild konnte nicht geladen werden.")}</p>
    </div>
  );
  return <>
    {loading && <div role="status" className="absolute inset-0 flex items-center justify-center text-slate-300 pointer-events-none">
      <Loader2 size={28} className="animate-spin" />
      <span className="sr-only">{t("Bild wird geladen…")}</span>
    </div>}
    <img data-testid="media-lightbox-image" src={src} alt={asset.alt_text || asset.filename}
      referrerPolicy="no-referrer" className={`max-w-full max-h-full w-auto h-auto object-contain ${loading ? "opacity-0" : "opacity-100"}`}
      onLoad={() => setLoading(false)}
      onError={() => {
        if (asset.cover_url && src !== asset.cover_url) setSrc(asset.cover_url);
        else { setFailed(true); setLoading(false); }
      }} />
  </>;
}

export default function MediaLightbox({ images, assetId, onNavigate, onClose, triggerRef }) {
  const { t } = useLanguage();
  const index = images.findIndex(a => a.id === assetId);
  const active = images[index];
  const canNavigate = images.length > 1;
  const move = (direction) => {
    if (active && canNavigate) onNavigate(images[(index + direction + images.length) % images.length].id);
  };
  return <Dialog.Root open={Boolean(active)} onOpenChange={open => { if (!open) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay data-testid="media-lightbox-backdrop" className="fixed inset-0 z-50 bg-black/85" />
      <Dialog.Content data-testid="media-lightbox" className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 flex flex-col w-[calc(100vw-2rem)] max-w-[1400px] h-[calc(100dvh-2rem)] rounded-xl border border-white/15 bg-slate-950 text-white shadow-2xl outline-none"
        onCloseAutoFocus={event => { event.preventDefault(); triggerRef.current?.focus(); }}
        onKeyDown={event => {
          if (event.altKey || event.ctrlKey || event.metaKey) return;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault(); move(event.key === "ArrowLeft" ? -1 : 1);
          }
        }}>
        <div className="flex items-center gap-3 px-4 py-3 pr-16 border-b border-white/10 min-w-0">
          <Dialog.Title className="text-sm font-medium truncate flex-1" title={active?.filename}>{active?.filename}</Dialog.Title>
          <span data-testid="media-lightbox-counter" aria-live="polite" className="shrink-0 text-xs text-slate-300 tabular-nums">{index + 1} / {images.length}</span>
        </div>
        <Dialog.Description className="sr-only">{t("Bildvorschau. Mit den Pfeiltasten blättern, mit Escape schließen.")}</Dialog.Description>
        <div className="relative flex-1 min-h-0 flex items-center justify-center p-3 sm:p-5 px-14 sm:px-20 overflow-hidden">
          {active && <PreviewImage key={active.id} asset={active} t={t} />}
          {canNavigate && <>
            <button type="button" data-testid="media-lightbox-previous" aria-label={t("Vorheriges Bild")}
              onClick={() => move(-1)} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 rounded-full p-2.5 bg-black/60 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
              <ChevronLeft size={24} />
            </button>
            <button type="button" data-testid="media-lightbox-next" aria-label={t("Nächstes Bild")}
              onClick={() => move(1)} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 rounded-full p-2.5 bg-black/60 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
              <ChevronRight size={24} />
            </button>
          </>}
        </div>
        <p className="text-xs text-slate-400 text-center px-4 py-2 border-t border-white/10">{t("Pfeiltasten zum Blättern · Escape zum Schließen")}</p>
        <Dialog.Close data-testid="media-lightbox-close" aria-label={t("Schließen")}
          className="absolute top-2 right-3 rounded-full p-2 hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
          <X size={22} />
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
